import React, {useMemo, useCallback} from 'react';
import {
  availJsonSchema,
  availUiSchema,
  dateJsonSchema,
  dateUiSchema,
  fileJsonSchema,
  fileUiSchema,
  rulesJsonSchema,
  rulesJsonSchemaForDays,
  rulesUiSchema,
  rulesUiSchemaForDays
} from '@/containers/ReviewBulkPointModifiers/ReviewBulkPointJSON';
import {Link} from '@/components/atoms';
import {PageContainer} from '@/components/organisms';
import {SectionTitle} from '@/components/molecules';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useAppDispatch, useAppSelector} from '@/store';
import {setBulkModifiers} from '@/store/slices/bulkPointSlice';
import {formatDateToMMDDYYYY, parseReviewScheduleDate} from '@/utils/commons';

const ReviewBulkPoint = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const bulkModifier = useAppSelector((state) => state?.bulkModifiers);
  const {
    actions = '',
    schedule = '',
    storeSelector = '',
    bulkModifierName = '',
    file = '',
    name = '',
    numberOfPoints = '',
    numberOfDays = '',
    targetMediaFile = [],
    scheduledAt = '',
    startDate = ''
  } = bulkModifier;

  const isPublished =
    bulkModifier?.status?.toString()?.toLowerCase() === 'published';

  const dateToDisplay = useMemo(() => {
    if (isPublished === true) {
      return scheduledAt;
    } else {
      return startDate ?? scheduledAt;
    }
  }, [scheduledAt, startDate, isPublished]);

  const scheduleDateObj = useMemo(
    () => parseReviewScheduleDate(dateToDisplay),
    [dateToDisplay]
  );
  const isScheduleDateInPast = useMemo(() => {
    if (!scheduleDateObj) return false;
    return scheduleDateObj.getTime() < Date.now();
  }, [scheduleDateObj]);

  const handleEditSection = useCallback(() => {
    const parsedStartDate = parseReviewScheduleDate(bulkModifier?.startDate);
    const normalizedStartDate = parsedStartDate
      ? formatDateToMMDDYYYY(parsedStartDate)
      : bulkModifier?.startDate;

    const fileValue = bulkModifier?.file || bulkModifier?.targetMediaFile?.[0];
    const updatedBulkModifier = {
      ...bulkModifier,
      startDate: normalizedStartDate,
      targetMediaFile: fileValue ? [fileValue] : [],
      storeList: bulkModifier?.file || bulkModifier?.storeList,
      numberOfPoints: bulkModifier?.numberOfPoints?.toString(),
      numberOfDays: bulkModifier?.numberOfDays?.toString(),
      ...(isScheduleDateInPast
        ? {
            schedule: '',
            startDate: '',
            endDate: ''
          }
        : {})
    };
    dispatch(setBulkModifiers(updatedBulkModifier));
    navigate(ROUTES.BULK_POINT_MODIFIER_CREATE);
  }, [bulkModifier, dispatch, navigate, isScheduleDateInPast]);

  // Helper component to render each section
  const RenderSection = useCallback(
    ({id, label, jsonSchema, uiSchema, data}) => (
      <div>
        <SectionTitle isBorderVisible={true} id={id} label={label} />
        <div className='pt-5'>
          <JsonFormsWrapper
            jsonschema={jsonSchema}
            uischema={uiSchema}
            data={data}
          />
        </div>
      </div>
    ),
    []
  );

  return (
    <PageContainer
      data-testid='review-bulk-point-modifiers-content'
      className='review-bulk-modifiers'>
      {(!bulkModifier?.status ||
        bulkModifier?.status?.toLowerCase() === 'draft') && (
        <div className='relative'>
          <div data-testid='targetSetupEdit'>
            <span className='absolute right-0'>
              <Link
                label='Edit Section'
                iconLeading='edit'
                onClick={handleEditSection}
              />
            </span>
          </div>
        </div>
      )}
      <div className='mt-[29px] text-base font-bold'>Bulk Modifier Setup </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={dateJsonSchema}
        uiSchema={dateUiSchema}
        data={{
          eventName: bulkModifierName || name,
          storeSelector: storeSelector
        }}
      />
      <div className='mt-[29px] text-base font-bold'>Rules </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={
          actions === 'Remove All Except'
            ? rulesJsonSchemaForDays
            : rulesJsonSchema
        }
        uiSchema={
          actions === 'Remove All Except' ? rulesUiSchemaForDays : rulesUiSchema
        }
        data={{
          action: actions,
          numberOfPoints: numberOfPoints,
          numberOfDays: numberOfDays
        }}
      />
      <div className='mt-[29px] text-base font-bold'>Availability </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={availJsonSchema}
        uiSchema={availUiSchema(schedule, dateToDisplay)}
        data={{
          schedule: schedule,
          date: dateToDisplay
        }}
      />
      {!isPublished && isScheduleDateInPast && (
        <div className='mt-2 text-sm text-[#d20000]'>
          Note: The scheduled date cannot be in the past. Please reselect
          'Schedule' on the setup page.
        </div>
      )}
      <div className='mt-[29px] text-base font-bold'>File </div>
      <RenderSection
        id='review'
        label=''
        jsonSchema={fileJsonSchema}
        uiSchema={fileUiSchema}
        data={{
          template: file || targetMediaFile?.[0] || ''
        }}
      />
    </PageContainer>
  );
};

export default React.memo(ReviewBulkPoint);
